import React, { useState } from 'react';
import { motion } from 'framer-motion';
import Icon from '../../../components/AppIcon';

const FAQSection = () => {
  const [openIndex, setOpenIndex] = useState(null);

  const faqs = [
    {
      question: "Quels sont vos délais de réponse?",
      answer: "Nous nous engageons à répondre à toutes les demandes dans les 24 heures ouvrables. Pour les demandes urgentes, nous offrons une réponse prioritaire dans l'heure qui suit. Notre équipe est disponible du lundi au vendredi de 8h à 18h."
    },
    {
      question: "Proposez-vous des consultations gratuites?",
      answer: "Oui, nous offrons une première consultation gratuite de 30 minutes pour tous nos services. Cette session nous permet de comprendre vos besoins et de vous proposer les solutions les plus adaptées. Vous pouvez réserver votre consultation via notre formulaire de contact ou par téléphone."
    },
    {
      question: "Quels modes de paiement acceptez-vous?",
      answer: "Nous acceptons les paiements en Francs Congolais (CDF) et en Dollars Américains (USD). Les modes de paiement incluent : virements bancaires, paiements mobiles (M-Pesa, Orange Money, Airtel Money), espèces, et chèques d'entreprise. Des facilités de paiement sont disponibles pour les contrats à long terme."
    },
    {
      question: "Couvrez-vous toute la RDC?",
      answer: "Nous avons des bureaux principaux à Kinshasa et Lubumbashi, et nous couvrons l'ensemble du territoire national pour la plupart de nos services. Pour certains services spécialisés, nous travaillons avec des partenaires locaux dans d'autres provinces pour garantir la même qualité de service."
    },
    {
      question: "Comment puis-je suivre ma demande?",
      answer: "Après soumission de votre demande, vous recevrez un numéro de référence unique par email et SMS. Vous pourrez suivre l'avancement de votre demande en temps réel via notre portail client en ligne ou en contactant notre service client. Des notifications automatiques vous seront envoyées à chaque étape importante."
    },
    {
      question: "Proposez-vous des services d'urgence?",
      answer: "Oui, nous offrons des services d'urgence 24/7 pour certaines catégories comme la sécurité, la maintenance, et les services juridiques critiques. Des frais supplémentaires peuvent s'appliquer pour les interventions en dehors des heures ouvrables. Contactez notre ligne d'urgence pour plus d'informations."
    },
    {
      question: "Quelles garanties offrez-vous?",
      answer: "Tous nos services sont couverts par notre garantie de satisfaction client. Nous offrons également des garanties spécifiques selon le type de service : garantie de qualité pour les fournitures, garantie de conformité pour les services juridiques, et assurance responsabilité civile pour tous nos services professionnels."
    },
    {
      question: "Comment obtenir un devis personnalisé?",
      answer: "Pour obtenir un devis détaillé et personnalisé, remplissez notre formulaire de contact en précisant vos besoins spécifiques. Notre équipe commerciale vous contactera dans les 24 heures pour discuter de votre projet et vous fournir une proposition détaillée incluant les coûts, les délais, et les conditions."
    }
  ];

  const toggleFAQ = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <section className="py-20 lg:py-32 bg-slate-100 dark:bg-slate-900 relative overflow-hidden border-t border-slate-200 dark:border-white/5">

      {/* Background */}
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom,_var(--tw-gradient-stops))] from-slate-800 via-slate-900 to-black opacity-0 dark:opacity-80" />
      <div className="absolute inset-0 opacity-0 dark:opacity-10 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] mix-blend-overlay"></div>

      {/* Animated Blob */}
      <motion.div
        animate={{ scale: [1, 1.2, 1], opacity: [0.1, 0.2, 0.1] }}
        transition={{ duration: 10, repeat: Infinity }}
        className="absolute top-0 left-0 w-[600px] h-[600px] bg-blue-600/10 rounded-full blur-[150px]"
      />

      <div className="w-full max-w-[1920px] mx-auto px-6 sm:px-12 lg:px-16 relative z-10">

        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-blue-500/10 border border-blue-500/20 rounded-full mb-6">
            <Icon name="HelpCircle" size={16} className="text-blue-400" />
            <span className="text-sm font-medium text-blue-600 dark:text-blue-300 uppercase tracking-wider">FAQ</span>
          </div>
          <h2 className="text-4xl md:text-6xl font-bold text-slate-900 dark:text-white mb-6">
            Questions{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-amber-400">
              Fréquentes
            </span>
          </h2>
          <p className="text-xl text-slate-600 dark:text-slate-400 max-w-3xl mx-auto leading-relaxed">
            Trouvez rapidement des réponses aux questions les plus courantes
          </p>
        </motion.div>

        {/* FAQ Items */}
        <div className="max-w-4xl mx-auto space-y-4">
          {faqs?.map((faq, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.05 }}
              className="bg-white/80 dark:bg-slate-800/40 backdrop-blur-xl border border-slate-200 dark:border-white/10 rounded-2xl overflow-hidden transition-all duration-300 hover:border-blue-500/30 hover:shadow-2xl"
            >
              <button
                onClick={() => toggleFAQ(index)}
                className="w-full flex items-center justify-between p-6 md:p-8 text-left transition-colors duration-300 hover:bg-slate-100 dark:hover:bg-slate-800/60"
              >
                <span className="text-base md:text-lg font-semibold text-slate-900 dark:text-white pr-4 flex-1 min-w-0">
                  {faq?.question}
                </span>
                <div className={`flex-shrink-0 w-10 h-10 rounded-full bg-white/80 dark:bg-white/5 border border-slate-200 dark:border-white/10 flex items-center justify-center transition-all duration-300 ${openIndex === index ? 'rotate-180 bg-blue-600/20 border-blue-500/30' : ''}`}>
                  <Icon
                    name="ChevronDown"
                    size={20}
                    className="text-blue-400"
                  />
                </div>
              </button>

              <div
                className={`transition-all duration-300 ease-in-out ${openIndex === index ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
                  } overflow-hidden`}
              >
                <div className="p-6 md:p-8 pt-0 md:pt-0">
                  <p className="text-base text-slate-600 dark:text-slate-400 leading-relaxed">
                    {faq?.answer}
                  </p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Bottom CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.3 }}
          className="mt-16 text-center"
        >
          <p className="text-lg text-slate-600 dark:text-slate-400 mb-8">
            Vous ne trouvez pas la réponse à votre question?
          </p>
          <div className="flex flex-col sm:flex-row gap-5 justify-center">
            <a
              href="mailto:info@orazek.com"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-blue-600 hover:bg-blue-500 text-white rounded-full transition-all duration-300 font-medium text-lg shadow-lg shadow-blue-600/25 hover:scale-105"
            >
              <Icon name="Mail" size={20} />
              Envoyez-nous un email
            </a>
            <a
              href="tel:+243818883531"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-transparent border border-slate-200 dark:border-white/20 text-slate-900 dark:text-white hover:bg-slate-100 dark:hover:bg-white/5 hover:border-slate-300 dark:hover:border-white/40 rounded-full transition-all duration-300 font-medium text-lg backdrop-blur-sm"
            >
              <Icon name="Phone" size={20} />
              Appelez-nous
            </a>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default FAQSection;
